<script setup lang="ts">
import { StickyNote, Trash2 } from 'lucide-vue-next';
import { ref } from 'vue';
import EmptyState from '@/components/shared/EmptyState.vue';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { DocumentNoteItem } from './types';

const props = defineProps<{
    notes: DocumentNoteItem[];
    currentUserId: string;
}>();

const emit = defineEmits<{
    add: [body: string];
    remove: [id: string];
}>();

const draft = ref('');

function submit() {
    const body = draft.value.trim();

    if (!body) {
        return;
    }

    emit('add', body);
    draft.value = '';
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}
</script>

<template>
    <div class="flex h-full flex-col">
        <div class="flex-1 space-y-3 p-4">
            <EmptyState
                v-if="props.notes.length === 0"
                :icon="StickyNote"
                title="Aucune note"
                description="Vos notes sur ce document apparaîtront ici."
                class="border-border/40"
            />

            <div
                v-for="note in props.notes"
                :key="note.id"
                class="group rounded-lg border border-border/50 bg-muted/20 p-3"
            >
                <div class="mb-1.5 flex items-start justify-between gap-2">
                    <div class="text-xs text-muted-foreground">
                        <span class="font-medium text-foreground">{{
                            note.author.name
                        }}</span>
                        · {{ formatDate(note.created_at) }}
                    </div>
                    <button
                        v-if="note.author.id === props.currentUserId"
                        class="shrink-0 rounded p-0.5 text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                        title="Supprimer la note"
                        @click="emit('remove', note.id)"
                    >
                        <Trash2 class="h-3.5 w-3.5" />
                    </button>
                </div>
                <p class="text-sm whitespace-pre-line text-foreground">
                    {{ note.body }}
                </p>
            </div>
        </div>

        <div class="shrink-0 border-t border-border/40 p-3">
            <Textarea
                v-model="draft"
                placeholder="Ajouter une note…"
                class="min-h-16 resize-none text-sm"
                @keydown.enter.meta.prevent="submit"
                @keydown.enter.ctrl.prevent="submit"
            />
            <div class="mt-2 flex items-center justify-between">
                <span class="text-[11px] text-muted-foreground"
                    >Ctrl/Cmd + Entrée pour envoyer</span
                >
                <Button
                    size="sm"
                    class="h-7 px-3 text-xs"
                    :disabled="!draft.trim()"
                    @click="submit"
                >
                    Ajouter
                </Button>
            </div>
        </div>
    </div>
</template>
